import React, { useId } from "react";
import { Minus, Plus } from "lucide-react";
import "./NumericField.css";

function toNumber(v) {
    const n = typeof v === "number" ? v : parseFloat(v);
    return Number.isFinite(n) ? n : 0;
}

function NumericField({
    label,
    value,
    onChange,
    unit = "in",
    step = 0.5,
    min = 0,
    max,
    disabled = false,
    hint,
    inline = false,
}) {
    const id = useId();
    const clamp = (n) => {
        let v = n;
        if (typeof min === "number") v = Math.max(min, v);
        if (typeof max === "number") v = Math.min(max, v);
        return v;
    };
    const apply = (n) => onChange?.(clamp(n));
    const inc = () => apply(toNumber(value) + step);
    const dec = () => apply(toNumber(value) - step);
    const handleInput = (e) => onChange?.(e.target.value);
    const handleBlur = (e) => apply(toNumber(e.target.value));
    const handleKey = (e) => {
        if (e.key === "ArrowUp") {
            e.preventDefault();
            inc();
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            dec();
        }
    };

    return (
        <div className={`nfield ${inline ? "nfield--inline" : ""} ${disabled ? "nfield--disabled" : ""}`}>
            <label className="nfield__label uppercase-label" htmlFor={id}>
                {label}
            </label>
            <div className="nfield__group">
                <input
                    id={id}
                    type="number"
                    inputMode="decimal"
                    step={step}
                    min={min}
                    max={max}
                    value={value}
                    onChange={handleInput}
                    onBlur={handleBlur}
                    onKeyDown={handleKey}
                    disabled={disabled}
                    className="nfield__input mono-num focus-ring"
                    aria-describedby={hint ? `${id}-hint` : undefined}
                />
                {unit && (
                    <span className="nfield__unit" aria-hidden="true">
                        {unit}
                    </span>
                )}
                <div className="nfield__steppers">
                    <button
                        type="button"
                        className="nfield__step focus-ring"
                        onClick={dec}
                        aria-label={`Decrease ${label}`}
                        disabled={disabled}
                        tabIndex={-1}
                    >
                        <Minus size={12} aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        className="nfield__step focus-ring"
                        onClick={inc}
                        aria-label={`Increase ${label}`}
                        disabled={disabled}
                        tabIndex={-1}
                    >
                        <Plus size={12} aria-hidden="true" />
                    </button>
                </div>
            </div>
            {hint && (
                <span id={`${id}-hint`} className="nfield__hint">
                    {hint}
                </span>
            )}
        </div>
    );
}

export default NumericField;
